<script setup lang="ts">
import { LucideChevronDown } from 'lucide-vue-next'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '~/components/ui/dropdown-menu'

import type { IBank } from '~/components/entity/bank'
import { getOrganizationNavLink } from '~/components/widget/bank/services-menu'

interface Props {
  organization: IBank
  type?: 'bank' | 'microcredit'
  limit?: number
  navItems: {
    name: string
    link: string
  }[]
}

const props = withDefaults(defineProps<Props>(), {
  limit: 7,
  type: 'bank'
})

const route = useRoute()
const isOpen = ref(false)
const filteredNavItems = computed(() =>
  (props.navItems || []).filter((i) => {
    const key = `${i.link}_count` as keyof IBank
    if (i.link === '#about') return true
    return (
      (props.organization?.[key] && props.organization[key] !== 0) ||
      (i.link === 'rates' &&
        (props.type === 'bank' ||
          ['salym-finance', 'elet-capital'].includes(props.organization?.slug)))
    )
  })
)
const fitRoute = computed(() => {
  return route.params.slug ? route.params.slug : route.params[props.type]
})

const fitNavItems = computed(() =>
  filteredNavItems.value.map((i) => ({
    ...i,
    link: getOrganizationNavLink(props.type, fitRoute.value as string, i.link)
  }))
)
const visibleItems = computed(() => fitNavItems.value.slice(0, props.limit))
const dropdownItems = computed(() => fitNavItems.value.slice(props.limit))
</script>

<template>
  <nav class="relative">
    <ul class="flex flex-col gap-3 sm:flex-row sm:gap-6">
      <li v-for="item in visibleItems" :key="item.name">
        <NuxtLinkLocale
          :to="item.link.replace(/_/g, '-')"
          :class="[
            { underline: route.path.replace(/^\/ky/, '') === item.link.replace(/_/g, '-') },
            'font-semibold leading-5 text-blue-600 hover:underline'
          ]"
        >
          {{ $t(item.name) }}
        </NuxtLinkLocale>
      </li>

      <li v-if="fitNavItems.length > props.limit" class="relative flex">
        <DropdownMenu v-model:open="isOpen">
          <DropdownMenuTrigger
            class="flex items-center font-semibold leading-5 text-blue-600 hover:underline"
            type="button"
          >
            {{ $t('widget.bank_services_menu.more') }}
            <LucideChevronDown
              class="ml-1 h-4 w-4 transition-transform duration-200"
              :class="{ 'rotate-180': isOpen }"
              aria-hidden="true"
            />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem v-for="item in dropdownItems" :key="item.name">
              <NuxtLinkLocale :to="item.link.replace(/_/g, '-')" class="block w-full">
                {{ $t(item.name) }}
              </NuxtLinkLocale>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </li>
    </ul>
  </nav>
</template>
